import { useEffect, useState } from 'react'
import ExperienceDisplay from './ExperienceDisplay'

const EXPERIENCE_COOKIE_KEY = 'user_experience_data'
const INITIAL_EXP_GAIN = 100 // 初回アクセス経験値
const REGULAR_EXP_GAIN = 10 // 通常アクセス経験値

interface ExperienceData {
  totalExperience: number
  accessCount: number
  lastAccessDate: string
}

// レベル計算（100XPごとにレベルアップ）
const calculateLevel = (exp: number): number => {
  return Math.floor(exp / 100) + 1
}

// 次のレベルまでの必要経験値
const getNextLevelExp = (level: number): number => {
  return level * 100
}

/**
 * 経験値システムコンポーネント（Redux不使用）
 */
export default function ExperienceSystem() {
  const [experienceData, setExperienceData] = useState<ExperienceData>({
    totalExperience: 0,
    accessCount: 0,
    lastAccessDate: new Date().toISOString(),
  })
  const [isInitialized, setIsInitialized] = useState(false)

  // Cookieから読み込み
  const loadFromCookie = (): ExperienceData | null => {
    if (typeof document === 'undefined') return null

    const savedData = document.cookie.split('; ').find((row) => row.startsWith(`${EXPERIENCE_COOKIE_KEY}=`))

    if (savedData) {
      try {
        const data = JSON.parse(decodeURIComponent(savedData.split('=')[1]))
        return data as ExperienceData
      } catch (e) {
        return null
      }
    }
    return null
  }

  // Cookieに保存
  const saveToCookie = (data: ExperienceData) => {
    if (typeof document === 'undefined') return

    const expires = new Date()
    expires.setFullYear(expires.getFullYear() + 1)
    document.cookie = `${EXPERIENCE_COOKIE_KEY}=${encodeURIComponent(
      JSON.stringify(data),
    )}; expires=${expires.toUTCString()}; path=/`
  }

  // 初期化とアクセス時の経験値付与
  useEffect(() => {
    const savedData = loadFromCookie()
    const now = new Date().toISOString()

    if (savedData) {
      // 既存ユーザー：通常の経験値を付与
      const newData: ExperienceData = {
        totalExperience: savedData.totalExperience + REGULAR_EXP_GAIN,
        accessCount: savedData.accessCount + 1,
        lastAccessDate: now,
      }
      setExperienceData(newData)
      saveToCookie(newData)
    } else {
      // 新規ユーザー：初回ボーナス経験値を付与
      const initialData: ExperienceData = {
        totalExperience: INITIAL_EXP_GAIN,
        accessCount: 1,
        lastAccessDate: now,
      }
      setExperienceData(initialData)
      saveToCookie(initialData)
    }

    setIsInitialized(true)
  }, [])

  if (!isInitialized) return null

  const currentLevel = calculateLevel(experienceData.totalExperience)
  const nextLevelExp = getNextLevelExp(currentLevel)
  const currentLevelExp = experienceData.totalExperience - (currentLevel - 1) * 100

  return <ExperienceDisplay experience={currentLevelExp} level={currentLevel} nextLevelExp={100} />
}
